import { createFileRoute, redirect } from '@tanstack/react-router'
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Calendar,
  Check,
  ChevronsUpDown,
  GripVertical,
  Hash,
  List,
  MoreHorizontal,
  Plus,
  Trash2,
  Type,
} from 'lucide-react'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { toast } from 'sonner'
import { v4 as uuid } from 'uuid'
import type { CustomFieldDefinitionDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-definitions'
import type {
  CustomFieldOption,
  CustomFieldType,
  EditableCustomFieldConfig,
} from '@/components/features/custom-fields/types'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  useCustomFieldDefinitionMutations,
  useCustomFieldDefinitions,
  useProjects,
  useWorkItemTypes,
} from '@/lib/db/rxdb/hooks'
import { useCustomFieldTabsForAssignment } from '@/components/features/custom-fields/useCustomFieldsForTask'
import useLoginStore from '@/store/LoginStore'
import { systemTabPropertiesId } from '@/lib/utils/systemTabIds'
import { LucideTabIcon } from '@/components/features/custom-fields/LucideTabIcon'
import {
  cleanConfig,
  isConfigEmpty,
  parseConfig,
  parseOptions,
} from '@/components/features/custom-fields/types'
import { usePageLayout } from '@/hooks/usePageLayout'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPageContainer, SettingsPageHeader } from '@/components/app/layout'
import { SettingsPageLoading } from '@/components/app/settings'
import { groupProjectsBySpace } from '@/lib/utils/groupProjectsBySpace'
import { ProjectIcon } from '@/lib/utils/projectIcon'

const FIELD_TYPE_ICONS: Record<string, typeof Type> = {
  string: Type,
  number: Hash,
  date: Calendar,
  list: List,
}

const FIELD_TYPE_LABELS: Record<string, string> = {
  string: 'Text',
  number: 'Number',
  date: 'Date',
  list: 'List',
}

interface FieldFormData {
  name: string
  fieldType: CustomFieldType
  isRequired: boolean
  customFieldTabId: string
  customFieldGroupId: string | null
  config: EditableCustomFieldConfig
  workItemTypeIds: Array<string>
  projectIds: Array<string>
  options: Array<CustomFieldOption>
}

const defaultFormData: FieldFormData = {
  name: '',
  fieldType: 'string',
  isRequired: false,
  // populated to the workspace's Properties tab id when the create dialog opens.
  customFieldTabId: '',
  customFieldGroupId: null,
  config: {},
  workItemTypeIds: [],
  projectIds: [],
  options: [],
}

type OptionDropPosition = 'before' | 'after'

interface OptionDropIndicator {
  id: string
  position: OptionDropPosition
}

interface OptionRowProps {
  option: CustomFieldOption
  index: number
  draggingId: string | null
  onDragStart: (id: string) => void
  onDragEnd: () => void
  onReorder: (sourceId: string, targetId: string, position: OptionDropPosition) => void
  dropIndicator: OptionDropIndicator | null
  onDropIndicatorChange: (next: OptionDropIndicator | null) => void
  onLabelChange: (label: string) => void
  onRemove: () => void
}

interface OptionDragData {
  type: 'custom-field-option-row'
  id: string
}

function extractOptionDragData(
  data: Record<string | symbol, unknown> | undefined,
): OptionDragData | null {
  if (!data) return null
  if (data['type'] !== 'custom-field-option-row') return null
  const id = data['id']
  if (typeof id !== 'string') return null
  return { type: 'custom-field-option-row', id }
}

function OptionRow({
  option,
  index,
  draggingId,
  onDragStart,
  onDragEnd,
  onReorder,
  dropIndicator,
  onDropIndicatorChange,
  onLabelChange,
  onRemove,
}: OptionRowProps) {
  const rowRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const element = rowRef.current
    const dragHandle = handleRef.current
    if (!element || !dragHandle) return

    return combine(
      draggable({
        element,
        dragHandle,
        getInitialData: () => ({ type: 'custom-field-option-row', id: option.id }),
        onDragStart: () => {
          onDragStart(option.id)
          onDropIndicatorChange(null)
        },
        onDrop: () => {
          onDragEnd()
          onDropIndicatorChange(null)
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          const data = extractOptionDragData(source.data)
          if (!data) return false
          return data.id !== option.id
        },
        getData: () => ({ type: 'custom-field-option-row', id: option.id }),
        onDropTargetChange: ({ location, source, self }) => {
          const data = extractOptionDragData(source.data)
          if (!data || data.id === option.id) {
            if (dropIndicator?.id === option.id) onDropIndicatorChange(null)
            return
          }
          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const placeAfter = pointerY > rect.top + rect.height / 2
          onDropIndicatorChange({ id: option.id, position: placeAfter ? 'after' : 'before' })
        },
        onDragLeave: () => {
          if (dropIndicator?.id === option.id) onDropIndicatorChange(null)
        },
        onDrop: ({ location, source, self }) => {
          const data = extractOptionDragData(source.data)
          onDropIndicatorChange(null)
          if (!data || data.id === option.id) return
          const innermost = location.current.dropTargets[0]
          if (innermost?.element !== self.element) return
          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const placeAfter = pointerY > rect.top + rect.height / 2
          onReorder(data.id, option.id, placeAfter ? 'after' : 'before')
        },
      }),
    )
  }, [
    option.id,
    onDragStart,
    onDragEnd,
    onReorder,
    onDropIndicatorChange,
    dropIndicator?.id,
  ])

  const isDragging = draggingId === option.id
  const showBefore = dropIndicator?.id === option.id && dropIndicator.position === 'before'
  const showAfter = dropIndicator?.id === option.id && dropIndicator.position === 'after'

  return (
    <div
      ref={rowRef}
      className={cn(
        'group relative flex items-center gap-2',
        isDragging && 'opacity-40',
      )}
    >
      {showBefore && (
        <div className="pointer-events-none absolute inset-x-0 -top-px z-10 h-0.5 bg-primary" />
      )}
      {showAfter && (
        <div className="pointer-events-none absolute inset-x-0 -bottom-px z-10 h-0.5 bg-primary" />
      )}
      <button
        type="button"
        ref={handleRef}
        aria-label={`Reorder option ${index + 1}`}
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground/60 transition',
          'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
          'hover:cursor-grab active:cursor-grabbing hover:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          isDragging && 'opacity-100 text-primary cursor-grabbing',
        )}
      >
        <GripVertical className="size-3.5" />
      </button>
      <Input
        value={option.label}
        onChange={(e) => onLabelChange(e.target.value)}
        placeholder={`Option ${index + 1}`}
        className="flex-1"
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={onRemove}
        aria-label={`Remove option ${index + 1}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

interface ProjectMultiSelectProject {
  id: string
  name: string
  icon: string | null
  iconType: string | null
  iconColor: string | null
}

interface ProjectMultiSelectSpace {
  id: string
  name: string
  icon: string | null
  iconType: string | null
  iconColor: string | null
}

interface ProjectMultiSelectGroup {
  space: ProjectMultiSelectSpace | null
  children: Array<ProjectMultiSelectProject>
}

interface ProjectMultiSelectProps {
  projectGroups: Array<ProjectMultiSelectGroup>
  selectedIds: Array<string>
  onToggle: (projectId: string) => void
  selectableCount: number
}

function ProjectMultiSelect({
  projectGroups,
  selectedIds,
  onToggle,
  selectableCount,
}: ProjectMultiSelectProps) {
  const { t } = useTranslation('settings')
  const [open, setOpen] = useState(false)
  const selectedInScopeCount = useMemo(() => {
    const ids = new Set(selectedIds)
    return projectGroups.reduce(
      (acc, g) => acc + g.children.filter((c) => ids.has(c.id)).length,
      0,
    )
  }, [projectGroups, selectedIds])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn(selectedInScopeCount === 0 && 'text-muted-foreground')}>
            {selectedInScopeCount === 0
              ? t('customFieldsPage.selectProjectsPlaceholder')
              : t('customFieldsPage.selectedOfTotal', { selected: selectedInScopeCount, total: selectableCount })}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={t('customFieldsPage.searchProjects')} />
          <CommandList>
            <CommandEmpty>{t('customFieldsPage.noProjectsFound')}</CommandEmpty>
            {projectGroups.map(({ space, children }, idx) => {
              const headingLabel = space?.name || (space ? 'Untitled space' : 'Other')
              const groupKey = space?.id ?? '__orphan__'
              return (
                <div key={groupKey}>
                  {idx > 0 && <CommandSeparator />}
                  <CommandGroup
                    heading={
                      <span className="flex items-center gap-1.5">
                        {space && (
                          <ProjectIcon
                            iconType={space.iconType}
                            icon={space.icon}
                            iconColor={space.iconColor}
                            size={12}
                          />
                        )}
                        <span>{headingLabel}</span>
                      </span>
                    }
                  >
                    {children.map((proj) => {
                      const isSelected = selectedIds.includes(proj.id)
                      const projName = proj.name || 'Untitled project'
                      return (
                        <CommandItem
                          key={proj.id}
                          value={`${headingLabel} ${projName}`}
                          onSelect={() => onToggle(proj.id)}
                        >
                          <Check
                            className={cn(
                              'mr-2 size-4',
                              isSelected ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <ProjectIcon
                            iconType={proj.iconType}
                            icon={proj.icon}
                            iconColor={proj.iconColor}
                            size={14}
                          />
                          <span className="ml-1.5">{projName}</span>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </div>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/custom-fields',
)({
  beforeLoad: ({ params }) => {
    if (!FEATURE_FLAGS.ENABLE_CUSTOM_FIELDS) {
      throw redirect({ to: '/$workspaceSlug/settings', params })
    }
  },
  component: CustomFieldSettingsPage,
})

export function CustomFieldSettingsPage() {
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const { t } = useTranslation('settings')
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const { customFieldDefinitions, isLoading } = useCustomFieldDefinitions()
  const { createCustomFieldDefinition, updateCustomFieldDefinition, deleteCustomFieldDefinition } =
    useCustomFieldDefinitionMutations()
  const { workItemTypes } = useWorkItemTypes()
  const { projects } = useProjects()
  const { tabsForAssignment, groupsForAssignment, isLoading: isTabsLoading } =
    useCustomFieldTabsForAssignment()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingField, setEditingField] = useState<CustomFieldDefinitionDocument | null>(null)
  const [formData, setFormData] = useState<FieldFormData>(defaultFormData)
  const [draggingOptionId, setDraggingOptionId] = useState<string | null>(null)
  const [optionDropIndicator, setOptionDropIndicator] = useState<OptionDropIndicator | null>(null)

  const activeFields = useMemo(
    () => (customFieldDefinitions ?? []).filter((f) => !f.isDeleted),
    [customFieldDefinitions],
  )

  const projectGroups = useMemo(() => groupProjectsBySpace(projects), [projects])
  const selectableProjectIds = useMemo(
    () => new Set(projectGroups.flatMap((g) => g.children.map((c) => c.id))),
    [projectGroups],
  )
  const legacyProjectIdCount = useMemo(
    () => formData.projectIds.filter((id) => !selectableProjectIds.has(id)).length,
    [formData.projectIds, selectableProjectIds],
  )

  // Default tab id for new fields: prefer the workspace's Properties system tab; if not yet
  // present in tabsForAssignment (initial sync race), fall back to the first available tab.
  const defaultTabId = useMemo(() => {
    if (!workspaceId) return ''
    const propertiesId = systemTabPropertiesId(workspaceId)
    if (tabsForAssignment.some((t) => t.id === propertiesId)) return propertiesId
    return tabsForAssignment[0]?.id ?? ''
  }, [workspaceId, tabsForAssignment])

  const openCreateDialog = useCallback(() => {
    setEditingField(null)
    setFormData({ ...defaultFormData, customFieldTabId: defaultTabId })
    setIsDialogOpen(true)
  }, [defaultTabId])

  const openEditDialog = useCallback((field: CustomFieldDefinitionDocument) => {
    setEditingField(field)
    setFormData({
      name: field.name,
      fieldType: field.fieldType,
      isRequired: field.isRequired,
      customFieldTabId: field.customFieldTabId,
      customFieldGroupId: field.customFieldGroupId,
      config: parseConfig(field.config),
      workItemTypeIds: field.workItemTypeIds,
      projectIds: field.projectIds,
      options: parseOptions(field.options),
    })
    setIsDialogOpen(true)
  }, [])

  // Settings form 3-state UI rule: tabs query in flight OR workspace has zero real tabs ⇒
  // disable Save (and the tab Select). If the chosen group's tab no longer matches, clear it.
  const groupsForChosenTab = useMemo(
    () => groupsForAssignment.filter((g) => g.customFieldTabId === formData.customFieldTabId),
    [groupsForAssignment, formData.customFieldTabId],
  )
  const tabsLoadedEmpty = !isTabsLoading && tabsForAssignment.length === 0
  const canSave = !isTabsLoading && tabsForAssignment.length > 0 && formData.customFieldTabId !== ''

  const handleSave = useCallback(async () => {
    if (!formData.name.trim()) {
      toast.error('Field name is required')
      return
    }

    const cleanedConfig = cleanConfig(formData.fieldType, formData.config)

    if (formData.fieldType === 'string') {
      if (cleanedConfig.maxLength != null && (!Number.isInteger(cleanedConfig.maxLength) || cleanedConfig.maxLength < 1)) {
        toast.error('Max length must be a positive integer')
        return
      }
    }

    if (formData.fieldType === 'number') {
      const { min, max } = cleanedConfig
      if (min != null && max != null && min > max) {
        toast.error('Min must be less than or equal to Max')
        return
      }
    }

    if (formData.fieldType === 'list') {
      if (formData.options.some((o) => o.label.trim() === '')) {
        toast.error('All options must have a label')
        return
      }
    }

    if (!canSave) {
      toast.error('Custom field tabs are not ready yet. Please try again.')
      return
    }
    if (formData.customFieldTabId.startsWith('__pending')) {
      toast.error('Cannot assign a pending tab. Wait for sync to complete.')
      return
    }

    // If the selected group no longer belongs to the chosen tab, drop the reference rather
    // than persist an inconsistent (tab, group) pair.
    const finalGroupId =
      formData.customFieldGroupId &&
      groupsForChosenTab.some((g) => g.id === formData.customFieldGroupId)
        ? formData.customFieldGroupId
        : null

    const data = {
      name: formData.name.trim(),
      fieldType: formData.fieldType,
      isRequired: formData.isRequired,
      customFieldTabId: formData.customFieldTabId,
      customFieldGroupId: finalGroupId,
      config: isConfigEmpty(cleanedConfig) ? null : JSON.stringify(cleanedConfig),
      workItemTypeIds: formData.workItemTypeIds,
      projectIds: formData.projectIds,
      options: formData.options.length > 0 ? JSON.stringify(formData.options) : null,
    }

    try {
      if (editingField) {
        await updateCustomFieldDefinition(editingField.id, data)
        toast.success('Custom field updated')
      } else {
        await createCustomFieldDefinition({
          ...data,
          sortOrder: String(Date.now()),
        } as any)
        toast.success('Custom field created')
      }
      setIsDialogOpen(false)
    } catch {
      toast.error('Failed to save custom field')
    }
  }, [formData, editingField, createCustomFieldDefinition, updateCustomFieldDefinition])

  const handleDelete = useCallback(
    async (field: CustomFieldDefinitionDocument) => {
      try {
        await deleteCustomFieldDefinition(field.id)
        toast.success('Custom field deleted')
      } catch {
        toast.error('Failed to delete custom field')
      }
    },
    [deleteCustomFieldDefinition],
  )

  const addOption = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      options: [
        ...prev.options,
        { id: uuid(), label: '', color: null, sortOrder: String(prev.options.length) },
      ],
    }))
  }, [])

  const updateOption = useCallback((index: number, label: string) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) => (i === index ? { ...opt, label } : opt)),
    }))
  }, [])

  const removeOption = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }))
  }, [])

  const reorderOptions = useCallback(
    (sourceId: string, targetId: string, position: OptionDropPosition) => {
      setFormData((prev) => {
        const fromIdx = prev.options.findIndex((o) => o.id === sourceId)
        const toIdx = prev.options.findIndex((o) => o.id === targetId)
        if (fromIdx === -1 || toIdx === -1) return prev
        const next = [...prev.options]
        const [moved] = next.splice(fromIdx, 1)
        if (!moved) return prev
        let insertAt = next.findIndex((o) => o.id === targetId)
        if (insertAt === -1) return prev
        if (position === 'after') insertAt += 1
        next.splice(insertAt, 0, moved)
        return {
          ...prev,
          options: next.map((opt, i) => ({ ...opt, sortOrder: String(i) })),
        }
      })
    },
    [],
  )

  const setConfigString = useCallback(
    (key: 'placeholder', rawValue: string) => {
      setFormData((prev) => {
        const next = { ...prev.config }
        if (rawValue === '') {
          delete next[key]
        } else {
          next[key] = rawValue
        }
        return { ...prev, config: next }
      })
    },
    [],
  )

  const setConfigNumber = useCallback(
    (key: 'maxLength' | 'min' | 'max', rawValue: number) => {
      setFormData((prev) => {
        const next = { ...prev.config }
        if (Number.isFinite(rawValue)) {
          next[key] = rawValue
        } else {
          delete next[key]
        }
        return { ...prev, config: next }
      })
    },
    [],
  )

  const toggleWit = useCallback((witId: string) => {
    setFormData((prev) => ({
      ...prev,
      workItemTypeIds: prev.workItemTypeIds.includes(witId)
        ? prev.workItemTypeIds.filter((id) => id !== witId)
        : [...prev.workItemTypeIds, witId],
    }))
  }, [])

  const toggleProject = useCallback((projectId: string) => {
    setFormData((prev) => ({
      ...prev,
      projectIds: prev.projectIds.includes(projectId)
        ? prev.projectIds.filter((id) => id !== projectId)
        : [...prev.projectIds, projectId],
    }))
  }, [])

  const clearLegacyProjectIds = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      projectIds: prev.projectIds.filter((id) => selectableProjectIds.has(id)),
    }))
  }, [selectableProjectIds])

  if (isLoading) {
    return <SettingsPageLoading />
  }

  return (
    <SettingsPageContainer innerClassName="space-y-6">
      <SettingsPageHeader
        title="Custom Fields"
        action={
          <Button onClick={openCreateDialog} size="sm">
            <Plus className="mr-1 size-4" />
            Add Field
          </Button>
        }
      />

      <div className="rounded-md border bg-card">
        {activeFields.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No custom fields defined yet. Click &quot;Add Field&quot; to create one.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Tab</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Scoping</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeFields.map((field) => {
                const Icon = FIELD_TYPE_ICONS[field.fieldType] ?? Type
                const witCount = field.workItemTypeIds.length
                const projCount = field.projectIds.length
                const fieldTab = tabsForAssignment.find((t) => t.id === field.customFieldTabId)
                const fieldGroup = field.customFieldGroupId
                  ? groupsForAssignment.find((g) => g.id === field.customFieldGroupId)
                  : null
                return (
                  <TableRow key={field.id}>
                    <TableCell className="font-medium">{field.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm">{FIELD_TYPE_LABELS[field.fieldType]}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {field.isRequired && <Badge variant="secondary">Required</Badge>}
                    </TableCell>
                    <TableCell>
                      {fieldTab ? (
                        <div className="flex items-center gap-1.5">
                          <LucideTabIcon icon={fieldTab.icon} className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm">{fieldTab.name}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-amber-500">Unknown tab</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {fieldGroup ? fieldGroup.name : '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 text-xs text-muted-foreground">
                        {witCount > 0 && <span>{witCount} WIT{witCount > 1 ? 's' : ''}</span>}
                        {witCount > 0 && projCount > 0 && <span>+</span>}
                        {projCount > 0 && <span>{projCount} project{projCount > 1 ? 's' : ''}</span>}
                        {witCount === 0 && projCount === 0 && <span className="text-amber-500">No scope</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(field)}>
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDelete(field)}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingField ? 'Edit Custom Field' : 'Create Custom Field'}</DialogTitle>
            <DialogDescription>
              {editingField
                ? 'Update the field configuration. Field type cannot be changed.'
                : 'Define a new custom field for your workspace.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label>{t('customFieldsPage.fieldName')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder={t('customFieldsPage.fieldNamePlaceholder')}
                maxLength={100}
              />
            </div>

            {/* Field Type */}
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={formData.fieldType}
                onValueChange={(v) =>
                  setFormData((p) => ({
                    ...p,
                    fieldType: v as CustomFieldType,
                    options: [],
                    config: {},
                  }))
                }
                disabled={!!editingField}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="list">List</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Type-specific config */}
            {formData.fieldType === 'string' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Placeholder</Label>
                  <Input
                    value={formData.config.placeholder ?? ''}
                    onChange={(e) => setConfigString('placeholder', e.target.value)}
                    placeholder="(uses field name)"
                    maxLength={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max length</Label>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={formData.config.maxLength ?? ''}
                    onChange={(e) => setConfigNumber('maxLength', e.currentTarget.valueAsNumber)}
                    placeholder={t('customFieldsPage.stringMaxPlaceholder')}
                  />
                </div>
              </div>
            )}

            {formData.fieldType === 'number' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Placeholder</Label>
                  <Input
                    value={formData.config.placeholder ?? ''}
                    onChange={(e) => setConfigString('placeholder', e.target.value)}
                    placeholder="(uses field name)"
                    maxLength={100}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Min</Label>
                    <Input
                      type="number"
                      value={formData.config.min ?? ''}
                      onChange={(e) => setConfigNumber('min', e.currentTarget.valueAsNumber)}
                      placeholder={t('customFieldsPage.numberMinPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max</Label>
                    <Input
                      type="number"
                      value={formData.config.max ?? ''}
                      onChange={(e) => setConfigNumber('max', e.currentTarget.valueAsNumber)}
                      placeholder="No maximum"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* List Options */}
            {formData.fieldType === 'list' && (
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="space-y-2">
                  {formData.options.map((opt, i) => (
                    <OptionRow
                      key={opt.id}
                      option={opt}
                      index={i}
                      draggingId={draggingOptionId}
                      onDragStart={setDraggingOptionId}
                      onDragEnd={() => setDraggingOptionId(null)}
                      onReorder={reorderOptions}
                      dropIndicator={optionDropIndicator}
                      onDropIndicatorChange={setOptionDropIndicator}
                      onLabelChange={(label) => updateOption(i, label)}
                      onRemove={() => removeOption(i)}
                    />
                  ))}
                  <Button variant="outline" size="sm" onClick={addOption}>
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add Option
                  </Button>
                </div>
              </div>
            )}

            {/* Required */}
            <div className="flex items-center justify-between">
              <Label>Required</Label>
              <Switch
                checked={formData.isRequired}
                onCheckedChange={(v) => setFormData((p) => ({ ...p, isRequired: v }))}
              />
            </div>

            {/* Tab + Group (replaces Display Location).
                3-state UI: A loading -> disabled select; B happy -> normal; C zero real tabs -> hard error banner. */}
            <div className="space-y-2">
              <Label>Tab</Label>
              {isTabsLoading ? (
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  Loading tabs…
                </div>
              ) : tabsLoadedEmpty ? (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  Custom field tabs are unavailable. Reload the page or contact support.
                </div>
              ) : (
                <Select
                  value={formData.customFieldTabId}
                  onValueChange={(v) =>
                    setFormData((p) => ({
                      ...p,
                      customFieldTabId: v,
                      // clearing the group when the tab changes prevents inconsistent (tab, group) state
                      customFieldGroupId: null,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a tab" />
                  </SelectTrigger>
                  <SelectContent>
                    {tabsForAssignment.map((tab) => (
                      <SelectItem key={tab.id} value={tab.id}>
                        {tab.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>Group</Label>
              <Select
                value={formData.customFieldGroupId ?? '__ungrouped__'}
                onValueChange={(v) =>
                  setFormData((p) => ({
                    ...p,
                    customFieldGroupId: v === '__ungrouped__' ? null : v,
                  }))
                }
                disabled={!canSave || groupsForChosenTab.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="(Ungrouped)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ungrouped__">(Ungrouped)</SelectItem>
                  {groupsForChosenTab.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {groupsForChosenTab.length === 0 && canSave && (
                <p className="text-xs text-muted-foreground">No groups in this tab. Field will be ungrouped.</p>
              )}
            </div>

            {/* Work Item Type Scoping — hidden while the product is task > subtask only. */}
            {FEATURE_FLAGS.ENABLE_WORK_ITEM_TYPES && (
              <div className="space-y-2">
                <Label>Task Types</Label>
                <div className="flex flex-wrap gap-2">
                  {(workItemTypes ?? []).filter((w) => !w.isDeleted).map((wit) => (
                    <Badge
                      key={wit.id}
                      variant={formData.workItemTypeIds.includes(wit.id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleWit(wit.id)}
                    >
                      {wit.name}
                    </Badge>
                  ))}
                </div>
                {formData.workItemTypeIds.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No types selected. Field will need at least one scope to be visible.
                  </p>
                )}
              </div>
            )}

            {/* Project Scoping */}
            <div className="space-y-2">
              <Label>Projects</Label>
              {projectGroups.length === 0 ? (
                <p className="text-xs text-muted-foreground">No projects available.</p>
              ) : (
                <ProjectMultiSelect
                  projectGroups={projectGroups}
                  selectedIds={formData.projectIds}
                  onToggle={toggleProject}
                  selectableCount={selectableProjectIds.size}
                />
              )}
              {formData.projectIds.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No projects selected. Field will need at least one scope to be visible.
                </p>
              )}
              {legacyProjectIdCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {legacyProjectIdCount} legacy scope
                  {legacyProjectIdCount > 1 ? 's' : ''} will be preserved.{' '}
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-foreground"
                    onClick={clearLegacyProjectIds}
                  >
                    Clear all
                  </button>
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave}>
              {editingField ? 'Save Changes' : 'Create Field'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsPageContainer>
  )
}
